const RICH_TEXT_MARKER = 'data-qstaff-rich-text="1"';

const escapeHtml = value => String(value ?? "")
  .replaceAll("&", "&amp;")
  .replaceAll("<", "&lt;")
  .replaceAll(">", "&gt;")
  .replaceAll('"', "&quot;")
  .replaceAll("'", "&#39;");

export const isContractRichText = value => typeof value === "string" && value.includes(RICH_TEXT_MARKER);

export const unwrapContractRichText = value => {
  if (!isContractRichText(value)) return "";
  const start = value.indexOf(">") + 1;
  const end = value.lastIndexOf("</div>");
  return start > 0 && end >= start ? value.slice(start, end) : "";
};

const normalizeFontSize = value => {
  const raw = String(value || "").trim();
  let match = raw.match(/^(\d+(?:\.\d+)?)pt$/i);
  if (match) {
    const size = Number(match[1]);
    if (Number.isFinite(size) && size >= 8 && size <= 28) return `${size}pt`;
  }
  match = raw.match(/^(\d+(?:\.\d+)?)px$/i);
  if (match) {
    const size = Number(match[1]) * 0.75;
    if (Number.isFinite(size) && size >= 8 && size <= 28) return `${Math.round(size * 10) / 10}pt`;
  }
  return null;
};

const legacyFontSize = value => ({
  "1": "8pt",
  "2": "9pt",
  "3": "10pt",
  "4": "12pt",
  "5": "14pt",
  "6": "18pt",
  "7": "24pt"
}[String(value || "").trim()] || null);

const hasBold = element => {
  if (["B", "STRONG"].includes(element.tagName)) return true;
  const weight = String(element.style?.fontWeight || "").toLowerCase();
  return weight === "bold" || Number.parseInt(weight, 10) >= 600;
};

const hasItalic = element => {
  if (["I", "EM"].includes(element.tagName)) return true;
  return String(element.style?.fontStyle || "").toLowerCase() === "italic";
};

const hasUnderline = element => {
  if (element.tagName === "U") return true;
  const decoration = String(element.style?.textDecoration || element.style?.textDecorationLine || "").toLowerCase();
  return decoration.includes("underline");
};

const textAlignmentStyle = element => {
  const textAlign = String(element.style?.textAlign || element.getAttribute?.("align") || "").toLowerCase();
  return ["left", "center", "right", "justify"].includes(textAlign) ? `text-align: ${textAlign}` : null;
};

const sanitizedStyles = element => {
  const styles = [];
  const tagName = element.tagName;
  const fontSize = normalizeFontSize(element.style?.fontSize) || (tagName === "FONT" ? legacyFontSize(element.getAttribute("size")) : null);
  const alignment = textAlignmentStyle(element);

  if (["DIV", "P", "TD", "TH"].includes(tagName) && alignment) styles.push(alignment);
  if (fontSize) styles.push(`font-size: ${fontSize}`);
  if (hasBold(element)) styles.push("font-weight: bold");
  if (hasItalic(element)) styles.push("font-style: italic");
  if (hasUnderline(element)) styles.push("text-decoration: underline");

  if (tagName === "TABLE") {
    styles.push("border-collapse: collapse");
    styles.push("width: 100%");
  }

  if (["TD", "TH"].includes(tagName)) {
    styles.push("border: 1px solid #000");
    styles.push("padding: 4px");
    styles.push("vertical-align: top");
    if (tagName === "TH" && !hasBold(element)) styles.push("font-weight: bold");
  }

  return styles;
};

const positiveIntegerAttribute = (element, name) => {
  const value = Number.parseInt(element.getAttribute?.(name), 10);
  return Number.isInteger(value) && value > 1 && value <= 20 ? String(value) : null;
};

const sanitizeElement = element => {
  const allowedBlockTags = new Set(["DIV", "P", "BR"]);
  const allowedInlineTags = new Set(["B", "STRONG", "I", "EM", "U", "SPAN", "FONT"]);
  const allowedTableTags = new Set(["TABLE", "THEAD", "TBODY", "TFOOT", "TR", "TD", "TH"]);

  Array.from(element.childNodes).forEach(child => {
    if (child.nodeType !== 1) return;
    sanitizeElement(child);

    if (!allowedBlockTags.has(child.tagName) && !allowedInlineTags.has(child.tagName) && !allowedTableTags.has(child.tagName)) {
      child.replaceWith(...Array.from(child.childNodes));
      return;
    }

    if (child.tagName === "BR") {
      Array.from(child.attributes).forEach(attribute => child.removeAttribute(attribute.name));
      return;
    }

    const styles = sanitizedStyles(child);
    const tag = allowedInlineTags.has(child.tagName) ? "span" : child.tagName.toLowerCase();
    const replacement = document.createElement(tag);
    if (styles.length) replacement.setAttribute("style", styles.join("; "));

    if (["TD", "TH"].includes(child.tagName)) {
      const colSpan = positiveIntegerAttribute(child, "colspan");
      const rowSpan = positiveIntegerAttribute(child, "rowspan");
      if (colSpan) replacement.setAttribute("colspan", colSpan);
      if (rowSpan) replacement.setAttribute("rowspan", rowSpan);
    }

    while (child.firstChild) replacement.appendChild(child.firstChild);
    child.replaceWith(replacement);
  });
};

export const sanitizeContractRichHtml = html => {
  const template = document.createElement("template");
  template.innerHTML = html || "";
  sanitizeElement(template.content);
  return template.innerHTML;
};

export const contractContentToEditorHtml = value => {
  if (isContractRichText(value)) return sanitizeContractRichHtml(unwrapContractRichText(value));
  const normalized = String(value || "").replace(/\r\n/g, "\n");
  return normalized
    .split("\n")
    .map(line => `<div>${line ? escapeHtml(line) : "<br>"}</div>`)
    .join("");
};

export const editorHtmlToContractContent = html =>
  `<div data-qstaff-rich-text="1">${sanitizeContractRichHtml(html)}</div>`;
